import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import CookiesServices from './services/CookiesService';
import Leaderboard from './pages/Top10/Top10';

const DashboardMain = lazy(() => import('./pages/Dashboard/Dash'));
const OldDashboard = lazy(() => import('./pages/Dash/Dash'));
const NationalDashboard = lazy(() => import('./pages/CEODashboard/NationalDashboard'));
const NewBusiness = lazy(() => import('./pages/NewBusiness/NewBusiness'));
const Renewals = lazy(() => import('./pages/Renewals/Renewals'));
const PolicyDetails = lazy(() => import('./pages/Policy/Policy'));
const News = lazy(() => import('./pages/News/News'));
const Datasync = lazy(() => import('./pages/Datasync/Datasync'));
const Birthdays = lazy(() => import('./pages/Birthdays/Birthdays'));
const Insights = lazy(() => import('./pages/Insights/Insights'));
const AdvisorSummary = lazy(() => import('./pages/AdvisorSummary/AdvisorSummary'));
const SalesPlan = lazy(() => import('./pages/SalesPlan/SalesPlan'));
const PasswordReset = lazy(() => import('./pages/PasswordReset/PasswordReset'));
const EProfile = lazy(() => import('./pages/AgentProfile/AgentProfile'));
const BC = lazy(() => import('./pages/BC/BC'));
const LeadsManagement = lazy(() => import('./pages/LeadsManagement/LeadsManagement'));
const PolicyInquiry = lazy(() => import('./pages/PolicyDetails/policy-inquiry/PolicyInquiry'));
const Welcome = lazy(() => import('./oneclick/pages/welcome/Welcome'));
const Validation = lazy(() => import('./oneclick/pages/validation-page/ValidationPage'));
const Policy = lazy(() => import('./oneclick/pages/policy/Policy'));
const OTPVerification = lazy(() => import('./oneclick/pages/otp/Otp'));
const Help = lazy(() => import('./oneclick/pages/help/Help'));
const Contact = lazy(() => import('./oneclick/pages/contact/Contact'));
const About = lazy(() => import('./oneclick/pages/about/About'));
const Home = lazy(() => import('./epolicy/pages/home/home'));
const Epolicy = lazy(() => import('./epolicy/pages/policy/policy'));
const Login =  lazy(() => import('./epolicy/pages/login/login')); 
const Verify =  lazy(() => import('./epolicy/pages/verify/verify'));
const ViewOrDownload = lazy(() => import('./epolicy/pages/policy/viewOrDownload'));
const Doc = lazy(() => import('./epolicy/pages/policy/doc'));
const DownloadConfirmation = lazy(() => import('./epolicy/pages/policy/downloadConfirmation'));
const EpolicyDetailed = lazy(() => import('./epolicy/pages/epolicy-detailed/epolicyDetailed'));
const EpolicySummary = lazy(() => import('./epolicy/pages/epolicy-summary/epolicySummary'));
const Report = lazy(() => import('./pages/Reports/ReportsPage'));
const IncomeStatement = lazy(() => import('./pages/IncomeStatement/IncomeStatement'));
const AgentDashboard = lazy(() => import('./pages/AgentDashboard/AgentDashboard'));
const LeaderDashboard = lazy(() => import('./pages/LeaderDashboard/LeaderDashboard'));
const Error = lazy(() => import('./pages/Error/Error'));
const Agreement = lazy(() => import('./pages/Error/Agreement/Agreement'));
const PDPA = lazy(() => import('./pages/Error/Agreement/PDPAAgreementForm/PDPAAgreementForm'));
const ImpersonationSelect = lazy(() => import('./pages/ImpersonationSelect/ImpersonationSelect'));
const SalesActivity = lazy(() => import('./pages/SalesActivity/SalesActivity'));
const ActivityDetailed = lazy(() => import('./pages/SalesActivity/ActivityDetailed/ActivityDetailed'));
const LeadDetailed = lazy(() => import('./pages/SalesActivity/LeadDetailed/LeadDetailed'));

const ProtectedRoute = ({ isLoggedIn, children }) => {
  return isLoggedIn ? children : <Navigate to="/" replace />;
};

ProtectedRoute.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

const cookies = new CookiesServices();

const getDefaultRoute = () => {
  if (cookies.get("errorCode") === "error") {
    return "/error";
  }
  const impersonationCount = Number(cookies.get("impersonationCount") || 0);

  if (impersonationCount > 1) {
    return "/impersonationSelect";
  }
  const role = cookies.get("role") || "";

  if (role.includes("CEO")||role.includes("SUPER_ADMIN")) {
    return "/nationalDashboard";
  }
  else if (role.includes("LOCATION_HEAD") || role.includes("TEAM_LEADER") || role.includes("FINANCE_PLANNER")) {
    return "/dashboard";
  }
  else if (role.includes("UNDERWRITER")) {
    return "/ePolicySummary";
  }
  else if (role.includes("ADMIN")) {
    return "/datasync";
  }
  else {
    return "/error";
  }
};

const AppRoutes = ({ isLoggedIn }) => {
  const [searchParams] = useSearchParams();
  const module = searchParams.get("code");
  return (
    <Suspense
      fallback={
        <div className="loading-overlay">
          <CircularProgress color="secondary" />
        </div>
      }
    >
      <Routes>
        <Route
          path="/"
          element={
            module === "pdpa-english" ||
              module === "pdpa-sinhala" ||
              module === "pdpa-tamil" ? (
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <PDPA />
              </ProtectedRoute>
            ) : (
              <Navigate to={getDefaultRoute()} replace />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              {process.env.REACT_APP_APPLICATION_VERSION === '1' ? (
                <OldDashboard />
              ) : (
                <DashboardMain />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/oldDashboard"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <OldDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/newDashboard"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <DashboardMain />
            </ProtectedRoute>
          }
        />
        <Route
          path="/nationalDashboard"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <NationalDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/newBusiness"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <NewBusiness />
            </ProtectedRoute>
          }
        />
        <Route
          path="/renewals"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Renewals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policyDetails"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <PolicyDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/anbpLeaderboard"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Leaderboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesPlan"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <SalesPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <News />
            </ProtectedRoute>
          }
        />
        <Route
          path="/datasync"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Datasync />
            </ProtectedRoute>
          }
        />
        <Route
          path="/birthdays"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Birthdays />
            </ProtectedRoute>
          }
        />
        <Route
          path="/insights"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Insights />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advisorSummary"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <AdvisorSummary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/passwordReset"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <PasswordReset />
            </ProtectedRoute>
          }
        />
        <Route
          path="/?code=passwordreset"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <PasswordReset />
            </ProtectedRoute>
          }
        />
        <Route
          path="/eProfile"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <EProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/eProfile/:code"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <BC />
            </ProtectedRoute>
          }
        />
        <Route
          path="/?code=eProfile"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <EProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leadsManagement"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <LeadsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policyDetails/Inquiry"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <PolicyInquiry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/qrpay"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Welcome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/validation"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Validation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policy"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Policy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/otp-verification"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <OTPVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Help />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Contact />
            </ProtectedRoute>
          }
        />
        <Route
          path="/about"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <About />
            </ProtectedRoute>
          }
        />
        <Route
          path="/?code=qrpay"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Welcome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/epolicy"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Epolicy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/login"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Login />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verify"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Verify />
            </ProtectedRoute>
          }
        />
        <Route
          path="/viewOrDownload"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <ViewOrDownload />
            </ProtectedRoute>
          }
        />
        <Route
          path="/doc"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Doc />
            </ProtectedRoute>
          }
        />
        <Route
          path="/downloadConfirmation"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <DownloadConfirmation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/epolicyDetailed"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <EpolicyDetailed />
            </ProtectedRoute>
          }
        />
        <Route
          path="/epolicySummary"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <EpolicySummary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Report"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Report />
            </ProtectedRoute>
          }
        />
        <Route
          path="/IncomeStatement"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <IncomeStatement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/MDRTDashboard"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <AgentDashboard />
            </ProtectedRoute> 
          }
        />
        <Route
          path="/MDRTLeaderboard"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <LeaderDashboard />
            </ProtectedRoute> 
          }
        />
        <Route
          path="/error"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Error />
            </ProtectedRoute> 
          }
        />
        <Route
          path="/agreement"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Agreement />
            </ProtectedRoute> 
          }
        />
        <Route
          path="/impersonationSelect"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <ImpersonationSelect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesActivity"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <SalesActivity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activityDetailed"
          element={ 
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <ActivityDetailed />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leadDetailed"
          element={ 
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <LeadDetailed />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

AppRoutes.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
};

export default AppRoutes;